import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Event, RecoveryStartResponse, RecoveryLinkResponse, RecoveryRestoreResponse } from "@uno/protocol";
import { AppContext } from "context";
import useStoreToast from "store/useStoreToast";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import { reducer, initialState } from "./reducer";
import { Mode } from "./types";
import EmailStep from "./steps/EmailStep";
import CodeStep from "./steps/CodeStep";
import QrBackupStep from "./steps/QrBackupStep";
import QrRestoreStep from "./steps/QrRestoreStep";
import PasskeyStep from "./steps/passkey";
import MethodPicker from "./components/MethodPicker";

export type SignupFlowHandle = {
    goBack: () => void;
    reset: () => void;
};

type SignupFlowProps = {
    header?: React.ReactNode;
    mode?: Mode;
    showModePill?: boolean;
    onComplete?: () => void;
    onRestore?: (token: string) => void;
    onDismiss?: () => void;
    onStepChange?: (step: string) => void;
    showNonCustodial?: boolean;
    theme?: "light" | "dark";
    className?: string;
};

const SignupFlow = React.forwardRef<SignupFlowHandle, SignupFlowProps>(({ header, mode: modeProp, showModePill, onComplete, onRestore, onDismiss, onStepChange, showNonCustodial, theme = "light", className }, ref) => {
    const { t } = useTranslation();
    const { socket } = React.useContext(AppContext);
    const showToast = useStoreToast((state) => state.show);
    const [internalMode, setInternalMode] = React.useState<Mode>(modeProp || "register");
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const codeInputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    const mode = modeProp || internalMode;
    const isLogin = mode === "login";

    React.useImperativeHandle(ref, () => ({
        goBack: () => dispatch({ type: "BACK_TO_EMAIL" }),
        reset: () => dispatch({ type: "BACK_TO_EMAIL" }),
    }));

    React.useEffect(() => {
        onStepChange?.(state.step);
    }, [state.step, onStepChange]);

    const handleModeSwitch = (next: Mode) => {
        setInternalMode(next);
        dispatch({ type: "BACK_TO_EMAIL" });
    };

    const handleSuccess = React.useCallback(() => {
        showToast(t(isLogin ? "signup.login_success" : "signup.done_title"), "success");

        if(isLogin) {
            return;
        }

        onComplete?.();
    }, [showToast, t, isLogin, onComplete]);

    const handleRestoreToken = React.useCallback((token: string) => {
        showToast(t("signup.login_success"), "success");
        onRestore?.(token);
    }, [showToast, t, onRestore]);

    const handleEmailSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        const trimmed = state.email.trim().toLowerCase();

        if(!trimmed || !socket) {
            return;
        }

        dispatch({ type: "SUBMIT" });

        socket.emit(Event.RecoveryStart, { email: trimmed, action: isLogin ? "restore" : "link" }, (response: RecoveryStartResponse) => {
            if(response.success) {
                dispatch({ type: "EMAIL_SUCCESS" });
                requestAnimationFrame(() => codeInputRefs.current[0]?.focus());
                return;
            }

            const errorKey = response.cooldown || response.error === "rate_limited"
                ? "signup.cooldown"
                : response.error === "invalid_email"
                    ? "signup.email_invalid"
                    : "signup.error";

            dispatch({ type: "FAIL", error: t(errorKey) });
        });
    };

    const handleCodeSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        if(state.code.length < 4 || !socket) {
            return;
        }

        dispatch({ type: "SUBMIT" });

        if(isLogin) {
            socket.emit(Event.RecoveryRestore, { provider: "email", email: state.email.trim().toLowerCase(), code: state.code }, (response: RecoveryRestoreResponse) => {
                if(response.success && response.token) {
                    dispatch({ type: "CODE_SUCCESS" });
                    handleRestoreToken(response.token);
                    return;
                }

                const errorKey = response.error === "wrong_code"
                    ? "signup.code_invalid"
                    : response.error === "code_expired"
                        ? "signup.code_expired"
                        : response.error === "no_backup"
                            ? "signup.no_backup"
                            : "signup.error";

                dispatch({ type: "FAIL", error: t(errorKey) });
            });
        } else {
            socket.emit(Event.RecoveryLink, { provider: "email", email: state.email.trim().toLowerCase(), code: state.code }, (response: RecoveryLinkResponse) => {
                if(response.success) {
                    dispatch({ type: "CODE_SUCCESS" });
                    handleSuccess();
                    return;
                }

                const errorKey = response.error === "wrong_code"
                    ? "signup.code_invalid"
                    : response.error === "code_expired"
                        ? "signup.code_expired"
                        : response.error === "too_many_attempts"
                            ? "signup.code_invalid"
                            : response.error === "identifier_already_linked"
                                ? "signup.email_already_linked"
                                : response.error === "recovery_already_set"
                                    ? "signup.recovery_already_set"
                                    : "signup.error";

                dispatch({ type: "FAIL", error: t(errorKey) });
            });
        }
    };

    return (
        <div className={`flex flex-col items-center gap-2.5 ${className || ""}`}>
            {showModePill && state.step === "email" && (
                <div className={`flex items-center gap-1 p-1 rounded-full ${
                    theme === "dark" ? "bg-white/10" : "bg-black/5"
                }`}>
                    {(["register", "login"] as const).map((tab) => (
                        <ButtonHapticWrapper key={tab}>
                            <button
                                type="button"
                                onClick={() => handleModeSwitch(tab)}
                                className={`relative px-4 py-1.5 rounded-full font-changa-one text-xs tracking-[0.12em] uppercase cursor-pointer transition-colors ${
                                    mode === tab ? "text-white" : (theme === "dark" ? "text-white/40 hover:text-white/60" : "text-current/40 hover:text-current/60")
                                }`}
                            >
                                {mode === tab && (
                                    <motion.div
                                        layoutId="signup-mode-pill"
                                        className={`absolute inset-0 rounded-full ${theme === "dark" ? "bg-white/20" : "bg-blue-900"}`}
                                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                    />
                                )}
                                <span className="relative z-10">{t(`signup.tab_${tab}`)}</span>
                            </button>
                        </ButtonHapticWrapper>
                    ))}
                </div>
            )}

            {header && state.step === "email" && header}

            {state.step === "email" && (
                <>
                    <EmailStep
                        email={state.email}
                        loading={state.loading}
                        error={state.error}
                        dispatch={dispatch}
                        onDismiss={onDismiss}
                        onSubmit={handleEmailSubmit}
                        theme={theme}
                        mode={mode}
                    />
                    {showNonCustodial && (
                        <MethodPicker
                            onOpenQrBackup={() => dispatch({ type: "SHOW_QR" })}
                            onOpenPasskey={window.PublicKeyCredential ? () => dispatch({ type: "SHOW_PASSKEY" }) : undefined}
                        />
                    )}
                </>
            )}

            {state.step === "code" && (
                <CodeStep
                    code={state.code}
                    loading={state.loading}
                    error={state.error}
                    dispatch={dispatch}
                    codeInputRefs={codeInputRefs}
                    onSubmit={handleCodeSubmit}
                    theme={theme}
                />
            )}

            {state.step === "qr" && !isLogin && (
                <QrBackupStep dispatch={dispatch} theme={theme} onSuccess={handleSuccess} />
            )}

            {state.step === "qr" && isLogin && (
                <QrRestoreStep theme={theme} onRestore={handleRestoreToken} />
            )}

            {state.step === "passkey" && (
                <PasskeyStep
                    dispatch={dispatch}
                    theme={theme}
                    mode={mode}
                    onSuccess={handleSuccess}
                    onRestore={handleRestoreToken}
                />
            )}
        </div>
    );
});

SignupFlow.displayName = "SignupFlow";

export default SignupFlow;
